import { motion } from "framer-motion";
import { Icon } from "@/components/icons";
import { DeviceMockup } from "@/components/DeviceMockup";

const EASE = [0.85, 0, 0.15, 1];

const accentText = { gold: "text-champagne", coral: "text-coral", cream: "text-cream" };
const accentBg = { gold: "bg-champagne", coral: "bg-coral", cream: "bg-cream" };

const Overline = ({ children, accent = "gold" }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, ease: EASE }}
    className="mb-5 flex items-center gap-3"
  >
    <span className={`h-px w-8 ${accentBg[accent]}`} />
    <span className={`text-xs font-semibold uppercase tracking-[0.25em] ${accentText[accent]}`}>
      {children}
    </span>
  </motion.div>
);

export const AnimatedTitle = ({ text, className = "" }) => {
  const lines = String(text).split("\n");
  return (
    <h1 className={`font-serif ${className}`} data-testid="slide-title">
      {lines.map((line, i) => (
        <span key={i} className="reveal-mask">
          <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.15 + i * 0.12 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </h1>
  );
};

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, ease: EASE, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const ImageFrame = ({ src, alt, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, scale: 1.06 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 1.1, ease: EASE }}
    className={`relative overflow-hidden rounded-2xl border border-white/10 ${className}`}
    style={{ boxShadow: "0 40px 120px -40px rgba(0,0,0,0.8)" }}
  >
    <img src={src} alt={alt || ""} className="h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-navy-900/70 via-transparent to-navy-900/20" />
    <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-champagne/20" />
  </motion.div>
);

const BulletCard = ({ icon, title, text, accent, i }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, ease: EASE, delay: 0.3 + i * 0.08 }}
    className="group rounded-2xl border border-white/10 bg-white/[0.03] p-6 backdrop-blur-sm transition-colors hover:border-champagne/40"
    data-testid="bullet-card"
  >
    <div className={`mb-4 inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 ${accent === "coral" ? "text-coral" : "text-champagne"} transition-transform group-hover:-translate-y-1`}>
      <Icon name={icon} className="h-5 w-5" />
    </div>
    <h3 className="mb-1.5 font-serif text-xl text-white">{title}</h3>
    <p className="text-sm leading-relaxed text-cream/70">{text}</p>
  </motion.div>
);

const StatBlock = ({ value, label, i, big }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, ease: EASE, delay: 0.3 + i * 0.1 }}
    data-testid="stat-block"
  >
    <div className={`font-serif leading-none text-champagne ${big ? "text-6xl sm:text-7xl lg:text-8xl" : "text-4xl sm:text-5xl"}`}>
      {value}
    </div>
    <div className="mt-3 max-w-[220px] text-sm leading-snug text-cream/70">{label}</div>
  </motion.div>
);

// ---- Layout templates ----

const TitleSlide = ({ s }) => (
  <div className="grid h-full w-full items-center gap-10 lg:grid-cols-[1.1fr_0.9fr]">
    <div>
      <Overline accent={s.accent}>{s.overline}</Overline>
      <AnimatedTitle text={s.title} className="text-5xl font-medium leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl" />
      {s.lead && (
        <FadeIn delay={0.6} className="mt-8 max-w-xl">
          <p className="text-lg font-light leading-relaxed text-cream/75">{s.lead}</p>
        </FadeIn>
      )}
    </div>
    {s.image && (
      <div className="relative hidden lg:block">
        <ImageFrame src={s.image} alt={s.imageAlt} className="aspect-[4/5]" />
      </div>
    )}
  </div>
);

const ContentWithBullets = ({ s, cols = "sm:grid-cols-3" }) => (
  <div className="flex h-full w-full flex-col justify-center">
    <div className="mb-10 max-w-3xl">
      <Overline accent={s.accent}>{s.overline}</Overline>
      <AnimatedTitle text={s.title} className="text-4xl font-medium leading-[1.02] tracking-tight text-white sm:text-5xl" />
      {s.body && (
        <FadeIn delay={0.5} className="mt-6 max-w-2xl">
          <p className="text-lg font-light leading-relaxed text-cream/75">{s.body}</p>
        </FadeIn>
      )}
    </div>
    <div className={`grid grid-cols-1 gap-4 ${cols}`}>
      {s.bullets?.map((b, i) => (
        <BulletCard key={i} {...b} accent={s.accent} i={i} />
      ))}
    </div>
  </div>
);

const StepsSlide = ({ s }) => (
  <div className="grid h-full w-full items-center gap-12 lg:grid-cols-[1fr_0.7fr]">
    <div>
      <Overline accent={s.accent}>{s.overline}</Overline>
      <AnimatedTitle text={s.title} className="mb-10 text-4xl font-medium leading-[1.02] tracking-tight text-white sm:text-5xl" />
      <div className="space-y-5">
        {s.steps?.map((step, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: EASE, delay: 0.3 + i * 0.1 }}
            className="flex items-start gap-5"
            data-testid="journey-step"
          >
            <span className="font-serif text-3xl leading-none text-champagne/50">{step.n}</span>
            <div className="border-l border-white/10 pl-5">
              <h3 className="font-serif text-xl text-white">{step.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-cream/70">{step.text}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
    {s.mockup && (
      <div className="hidden lg:block">
        <DeviceMockup type={s.mockup} image={s.mockImage} title={s.mockTitle} />
      </div>
    )}
  </div>
);

const StatsSlide = ({ s }) => (
  <div className="flex h-full w-full flex-col justify-center">
    <div className="mb-12 max-w-3xl">
      <Overline accent={s.accent}>{s.overline}</Overline>
      <AnimatedTitle text={s.title} className="text-4xl font-medium leading-[1.02] tracking-tight text-white sm:text-6xl" />
      {s.body && (
        <FadeIn delay={0.5} className="mt-6 max-w-2xl">
          <p className="text-lg font-light leading-relaxed text-cream/75">{s.body}</p>
        </FadeIn>
      )}
    </div>
    <div className="grid grid-cols-2 gap-10 lg:grid-cols-4">
      {s.stats?.map((st, i) => (
        <StatBlock key={i} {...st} i={i} big />
      ))}
    </div>
  </div>
);

const VisionSlide = ({ s }) => (
  <div className="grid h-full w-full items-center gap-12 lg:grid-cols-[1fr_0.9fr]">
    <div>
      <Overline accent={s.accent}>{s.overline}</Overline>
      <AnimatedTitle text={s.title} className="text-4xl font-medium leading-[1.02] tracking-tight text-white sm:text-5xl" />
      {s.body && (
        <FadeIn delay={0.5} className="mt-6 max-w-xl">
          <p className="text-lg font-light leading-relaxed text-cream/75">{s.body}</p>
        </FadeIn>
      )}
      <div className="mt-10 grid grid-cols-3 gap-6">
        {s.stats?.map((st, i) => (
          <StatBlock key={i} {...st} i={i} />
        ))}
      </div>
    </div>
    {s.image && <ImageFrame src={s.image} alt={s.imageAlt} className="hidden aspect-[4/5] lg:block" />}
  </div>
);

const BusinessSlide = ({ s }) => (
  <div className="flex h-full w-full flex-col justify-center">
    <div className="mb-10 max-w-3xl">
      <Overline accent={s.accent}>{s.overline}</Overline>
      <AnimatedTitle text={s.title} className="text-4xl font-medium leading-[1.02] tracking-tight text-white sm:text-5xl" />
      {s.body && (
        <FadeIn delay={0.5} className="mt-6 max-w-2xl">
          <p className="text-lg font-light leading-relaxed text-cream/75">{s.body}</p>
        </FadeIn>
      )}
    </div>
    <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
      <div className="grid gap-4 sm:grid-cols-3">
        {s.bullets?.map((b, i) => (
          <BulletCard key={i} {...b} accent={s.accent} i={i} />
        ))}
      </div>
      <div className="flex flex-col justify-center gap-8 rounded-2xl border border-champagne/20 bg-champagne/[0.06] p-8">
        {s.stats?.map((st, i) => (
          <StatBlock key={i} {...st} i={i} />
        ))}
      </div>
    </div>
  </div>
);

const UseCasesSlide = ({ s }) => (
  <div className="flex h-full w-full flex-col justify-center">
    <div className="mb-10 max-w-3xl">
      <Overline accent={s.accent}>{s.overline}</Overline>
      <AnimatedTitle text={s.title} className="text-4xl font-medium leading-[1.02] tracking-tight text-white sm:text-5xl" />
    </div>
    <div className="grid gap-5 lg:grid-cols-3">
      {s.bullets?.map((b, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE, delay: 0.3 + i * 0.1 }}
          className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-7"
          data-testid="usecase-card"
        >
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl border border-champagne/30 text-champagne">
            <Icon name={b.icon} className="h-5 w-5" />
          </div>
          <h3 className="mb-2 font-serif text-2xl text-white">{b.title}</h3>
          <p className="text-sm leading-relaxed text-cream/70">{b.text}</p>
          <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-champagne/5 blur-2xl transition-all group-hover:bg-champagne/10" />
        </motion.div>
      ))}
    </div>
  </div>
);

const FaqSlide = ({ s }) => (
  <div className="flex h-full w-full flex-col justify-center">
    <div className="mb-10 max-w-3xl">
      <Overline accent={s.accent}>{s.overline}</Overline>
      <AnimatedTitle text={s.title} className="text-4xl font-medium leading-[1.02] tracking-tight text-white sm:text-5xl" />
    </div>
    <div className="grid gap-5 lg:grid-cols-2">
      {s.faq?.map((f, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: EASE, delay: 0.3 + i * 0.08 }}
          className="rounded-2xl border border-white/10 bg-white/[0.03] p-6"
          data-testid="faq-item"
        >
          <h3 className="mb-2 font-serif text-lg text-champagne">{f.q}</h3>
          <p className="text-sm leading-relaxed text-cream/75">{f.a}</p>
        </motion.div>
      ))}
    </div>
  </div>
);

const CtaSlide = ({ s }) => (
  <div className="relative flex h-full w-full flex-col items-center justify-center text-center">
    {s.image && (
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden rounded-3xl opacity-30">
        <img src={s.image} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-navy-900/70" />
      </div>
    )}
    <Overline accent={s.accent}>{s.overline}</Overline>
    <AnimatedTitle text={s.title} className="max-w-4xl text-5xl font-medium leading-[1.02] tracking-tight text-white sm:text-6xl" />
    {s.body && (
      <FadeIn delay={0.6} className="mt-8 max-w-2xl">
        <p className="text-lg font-light leading-relaxed text-cream/80">{s.body}</p>
      </FadeIn>
    )}
    <FadeIn delay={0.8} className="mt-10 flex flex-wrap items-center justify-center gap-4">
      {s.bullets?.map((b, i) => (
        <div key={i} className="flex items-center gap-2 rounded-full border border-champagne/30 bg-white/[0.04] px-5 py-2.5">
          <Icon name={b.icon} className="h-4 w-4 text-champagne" />
          <span className="text-sm text-cream">{b.text}</span>
        </div>
      ))}
    </FadeIn>
  </div>
);

const TutorialSlide = ({ s }) => (
  <div className="grid h-full w-full items-center gap-12 lg:grid-cols-[1fr_0.7fr]">
    <div>
      <div className="mb-6 flex items-baseline gap-4">
        <span className="font-serif text-7xl leading-none text-champagne/30">{s.step}</span>
        <Overline accent={s.accent}>{s.overline}</Overline>
      </div>
      <AnimatedTitle text={s.title} className="text-4xl font-medium leading-[1.02] tracking-tight text-white sm:text-6xl" />
      {s.body && (
        <FadeIn delay={0.5} className="mt-6 max-w-lg">
          <p className="text-lg font-light leading-relaxed text-cream/75">{s.body}</p>
        </FadeIn>
      )}
    </div>
    <div className="flex justify-center">
      <DeviceMockup type={s.mockup} image={s.mockImage} title={s.mockTitle} />
    </div>
  </div>
);

export const SlideRenderer = ({ slide }) => {
  switch (slide.type) {
    case "title":
      return <TitleSlide s={slide} />;
    case "problem":
    case "solution":
    case "features":
    case "list":
      return <ContentWithBullets s={slide} />;
    case "steps":
      return <StepsSlide s={slide} />;
    case "stats":
      return <StatsSlide s={slide} />;
    case "vision":
      return <VisionSlide s={slide} />;
    case "business":
      return <BusinessSlide s={slide} />;
    case "usecases":
      return <UseCasesSlide s={slide} />;
    case "faq":
      return <FaqSlide s={slide} />;
    case "cta":
      return <CtaSlide s={slide} />;
    case "tutorial":
      return <TutorialSlide s={slide} />;
    default:
      return <ContentWithBullets s={slide} />;
  }
};
